import type { TodoPriority, TodoRepeat, TodoTask } from "../types";

export type TodoView = "inbox" | "my" | "team" | "people" | "completed" | `person:${string}` | `project:${string}`;

export function localDateKey(value = new Date()): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseLocalDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function nextRepeatDate(dueDate: string, repeat: TodoRepeat): string {
  if (repeat === "none") return dueDate;
  const date = parseLocalDate(dueDate) ?? new Date();
  if (repeat === "daily") date.setDate(date.getDate() + 1);
  if (repeat === "weekly") date.setDate(date.getDate() + 7);
  if (repeat === "monthly") {
    const targetDay = date.getDate();
    date.setDate(1);
    date.setMonth(date.getMonth() + 1);
    date.setDate(Math.min(targetDay, new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()));
  }
  return localDateKey(date);
}

export function matchesTodoView(task: TodoTask, view: TodoView, currentUserId: string): boolean {
  const completed = Boolean(task.completedAt);
  if (view === "completed") return completed;
  if (completed) return false;
  if (view === "inbox") return task.projectId === null;
  if (view === "my") return task.teamWide || task.assigneeIds.includes(currentUserId);
  if (view === "team") return task.teamWide || task.assigneeIds.some((id) => id !== currentUserId);
  if (view === "people") return false;
  if (view.startsWith("person:")) return task.teamWide || task.assigneeIds.includes(view.slice("person:".length));
  return task.projectId === view.slice("project:".length);
}

const priorityRank: Record<TodoPriority, number> = { high: 0, medium: 1, low: 2, none: 3 };

export function sortTodoTasks(tasks: TodoTask[]): TodoTask[] {
  return [...tasks].sort((left, right) => {
    if (Boolean(left.completedAt) !== Boolean(right.completedAt)) return left.completedAt ? 1 : -1;
    const leftDue = left.dueDate || "9999-12-31";
    const rightDue = right.dueDate || "9999-12-31";
    return leftDue.localeCompare(rightDue)
      || (left.dueTime || "99:99").localeCompare(right.dueTime || "99:99")
      || priorityRank[left.priority] - priorityRank[right.priority]
      || right.createdAt.localeCompare(left.createdAt);
  });
}

export function formatTodoDate(value: string, today = localDateKey()): string {
  const date = parseLocalDate(value);
  if (!date) return "";
  if (value === today) return "Today";
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (value === localDateKey(tomorrow)) return "Tomorrow";
  return date.toLocaleDateString([], { month: "short", day: "numeric", year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
}

export function taskSearchText(task: TodoTask): string {
  return [task.title, task.notes, ...task.labels, ...task.subtasks.map((subtask) => subtask.title)].join(" ").toLocaleLowerCase();
}
